import { TripStatus } from "@safiri/shared";
import type { MessageKey } from "../i18n";
import type { LatLng, RoutePoint, TripData } from "../types/trip";

export type Phase =
  | "finding"
  | "toPickup"
  | "atPickup"
  | "onTrip"
  | "atStop"
  | "arrived"
  | "completed"
  | "cancelled"
  | "noDriver";

export function phaseOf(status?: TripStatus): Phase {
  switch (status) {
    case TripStatus.ACCEPTED:
      return "toPickup";
    case TripStatus.DRIVER_ARRIVED:
      return "atPickup";
    case TripStatus.IN_PROGRESS:
    case TripStatus.INTERMEDIATE_STOP_SKIPPED:
      return "onTrip";
    case TripStatus.INTERMEDIATE_STOP_ARRIVED:
      return "atStop";
    case TripStatus.DESTINATION_ARRIVED:
      return "arrived";
    case TripStatus.COMPLETED:
      return "completed";
    case TripStatus.CANCELLED:
      return "cancelled";
    case TripStatus.NOT_FOUND:
      return "noDriver";
    default:
      return "finding";
  }
}

/** Phases where the page keeps listening and polling. */
export const isLivePhase = (phase: Phase) =>
  phase !== "completed" && phase !== "cancelled" && phase !== "noDriver";

/** Phases where a driver is attached and visible on the map. */
export const hasDriverPhase = (phase: Phase) =>
  phase === "toPickup" ||
  phase === "atPickup" ||
  phase === "onTrip" ||
  phase === "atStop" ||
  phase === "arrived";

export type Tone = "neutral" | "progress" | "success" | "danger";

export const phaseTone: Record<Phase, Tone> = {
  finding: "neutral",
  toPickup: "progress",
  atPickup: "progress",
  onTrip: "progress",
  atStop: "progress",
  arrived: "success",
  completed: "success",
  cancelled: "danger",
  noDriver: "danger",
};

export const phaseTitle: Record<Phase, MessageKey> = {
  finding: "phaseFinding",
  toPickup: "phaseToPickup",
  atPickup: "phaseAtPickup",
  onTrip: "phaseOnTrip",
  atStop: "phaseAtStop",
  arrived: "phaseArrived",
  completed: "phaseCompleted",
  cancelled: "phaseCancelled",
  noDriver: "phaseNoDriver",
};

export function pointOf(stop?: RoutePoint): LatLng | null {
  const lat = stop?.geoLocation?.coordinates?.[1] ?? stop?.latitude;
  const lng = stop?.geoLocation?.coordinates?.[0] ?? stop?.longitude;
  return typeof lat === "number" && typeof lng === "number" ? { lat, lng } : null;
}

export type StopState = "done" | "next" | "upcoming" | "skipped";

export interface StopView {
  kind: "pickup" | "stop" | "dropoff";
  index: number;
  address: string;
  state: StopState;
}

/**
 * Where each planned stop stands. finalTripRoute holds the pickup plus every
 * stop already reached or skipped, in order, so its length is the index of
 * the stop being driven to.
 */
export function stopViews(trip: TripData, phase: Phase): StopView[] {
  const planned = trip.initialTripRoute ?? [];
  const reached = trip.finalTripRoute ?? [];
  const last = planned.length - 1;
  const finished = phase === "arrived" || phase === "completed";
  const started = phase === "onTrip" || phase === "atStop" || finished;
  const nextIndex = started ? Math.min(Math.max(reached.length, 1), last) : 0;

  return planned.map((stop, index) => {
    const kind = index === 0 ? "pickup" : index === last ? "dropoff" : "stop";
    let state: StopState = "upcoming";
    if (finished) {
      state = reached[index]?.skipped ? "skipped" : "done";
    } else if (started && index < nextIndex) {
      state = reached[index]?.skipped ? "skipped" : "done";
    } else if (
      index === nextIndex &&
      (phase === "toPickup" || phase === "atPickup" || started)
    ) {
      state = "next";
    }
    return { kind, index, address: stop.address || stop.name || "—", state };
  });
}

export function isHeadingToIntermediateStop(trip: TripData, phase: Phase): boolean {
  const planned = trip.initialTripRoute?.length ?? 0;
  if (phase !== "onTrip" || planned <= 2) return false;
  return (trip.finalTripRoute?.length ?? 0) < planned - 1;
}

export function statusTime(trip: TripData, status: TripStatus): string | null {
  const history = trip.statusHistory ?? [];
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].status === status) return history[i].at;
  }
  return null;
}
